import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useGMB } from '../contexts/GMBContext';
import { useLanguage } from '../contexts/LanguageContext';
import { siteAPI } from '../lib/api';
import { 
  LayoutDashboard, FileText, MessageSquare, BarChart3, 
  Calendar, Settings, LogOut, Menu, X, Bell, Search,
  MapPin, Star, ChevronDown, ChevronRight, Globe, Zap,
  MessagesSquare, Sparkles, Palette, Users, Gift, Wallet, CreditCard
} from 'lucide-react';

const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { gmbProfile, isConnected } = useGMB();
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileDropdown, setProfileDropdown] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [engagementOpen, setEngagementOpen] = useState(true);
  const [autoSettingOpen, setAutoSettingOpen] = useState(true);

  useEffect(() => {
    const fetchSiteStatus = async () => {
      try {
        const res = await siteAPI.getStatus();
        // Check if admin is logged in (either via main auth or admin auth)
        const isAdmin = user?.is_admin || (() => {
          try {
            const adminUser = JSON.parse(localStorage.getItem('admin_user') || '{}');
            return adminUser?.is_admin === true;
          } catch { return false; }
        })();
        if (res.data.maintenance_enabled && !isAdmin) {
          setMaintenanceMode(true);
          navigate('/maintenance');
          return;
        }
        if (res.data.alert_enabled && res.data.alert_message) {
          setAlertMessage(res.data.alert_message);
        }
      } catch { /* ignore */ }
    };
    fetchSiteStatus();
  }, [navigate, user]);

  // Auto-expand section based on current path
  useEffect(() => {
    const path = location.pathname;
    if (['/dashboard/post-generator', '/dashboard/review-reply', '/dashboard/schedule'].some(p => path.startsWith(p))) {
      setEngagementOpen(true);
    }
    if (['/dashboard/auto-post', '/dashboard/auto-reply'].some(p => path.startsWith(p))) {
      setAutoSettingOpen(true);
    }
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(path);
  };

  const engagementPaths = ['/dashboard/post-generator', '/dashboard/review-reply', '/dashboard/schedule'];
  const autoSettingPaths = ['/dashboard/auto-post', '/dashboard/auto-reply'];
  const isEngagementActive = engagementPaths.some(p => location.pathname.startsWith(p));
  const isAutoSettingActive = autoSettingPaths.some(p => location.pathname.startsWith(p));
  const isCreativeActive = location.pathname.startsWith('/dashboard/creative-templates');
  const isTeamActive = location.pathname.startsWith('/dashboard/team');
  const referralPaths = ['/dashboard/referrals'];
  const isReferralActive = referralPaths.some(p => location.pathname.startsWith(p));

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/30 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-[#DADCE0] transform transition-transform duration-200 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-4 border-b border-[#DADCE0]">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#1A73E8] rounded-lg flex items-center justify-center">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
                GMB Autopilot
              </span>
            </Link>
          </div>

          {/* Connected GMB Profile Mini Card */}
          {isConnected && gmbProfile && (
            <div className="p-4 border-b border-[#DADCE0]">
              <div className="flex items-center gap-3">
                <img 
                  src={gmbProfile.profileImage} 
                  alt={gmbProfile.name}
                  className="w-10 h-10 rounded-lg object-cover"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#202124] truncate">{gmbProfile.name}</p>
                  <div className="flex items-center gap-1">
                    <Star className="w-3 h-3 fill-[#F9AB00] text-[#F9AB00]" />
                    <span className="text-xs text-[#5F6368]">{gmbProfile.rating}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {/* Dashboard */}
            <Link
              to="/dashboard"
              onClick={() => setSidebarOpen(false)}
              className={`sidebar-item ${isActive('/dashboard') && location.pathname === '/dashboard' ? 'active' : ''}`}
              data-testid="nav-dashboard"
            >
              <LayoutDashboard className="w-5 h-5" />
              {t('dashboard')}
            </Link>

            {/* Engagement Section */}
            <div className="pt-2">
              <button
                onClick={() => setEngagementOpen(!engagementOpen)}
                className={`sidebar-item w-full justify-between ${isEngagementActive ? 'text-[#1A73E8] font-medium' : ''}`}
                data-testid="nav-engagement-toggle"
              >
                <span className="flex items-center gap-3">
                  <MessagesSquare className="w-5 h-5" />
                  Engagement
                </span>
                <ChevronRight className={`w-4 h-4 transition-transform ${engagementOpen ? 'rotate-90' : ''}`} />
              </button>
              {engagementOpen && (
                <div className="ml-4 mt-1 space-y-0.5 border-l-2 border-[#E8F0FE] pl-3">
                  <Link
                    to="/dashboard/post-generator"
                    onClick={() => setSidebarOpen(false)}
                    className={`sidebar-item text-sm ${isActive('/dashboard/post-generator') ? 'active' : ''}`}
                    data-testid="nav-post-generator"
                  >
                    <FileText className="w-4 h-4" />
                    Create Post
                  </Link>
                  <Link
                    to="/dashboard/review-reply"
                    onClick={() => setSidebarOpen(false)}
                    className={`sidebar-item text-sm ${isActive('/dashboard/review-reply') ? 'active' : ''}`}
                    data-testid="nav-review-reply"
                  >
                    <MessageSquare className="w-4 h-4" />
                    Review Replies
                  </Link>
                  <Link
                    to="/dashboard/schedule"
                    onClick={() => setSidebarOpen(false)}
                    className={`sidebar-item text-sm ${isActive('/dashboard/schedule') ? 'active' : ''}`}
                    data-testid="nav-schedule"
                  >
                    <Calendar className="w-4 h-4" />
                    Schedule
                  </Link>
                </div>
              )}
            </div>

            {/* Auto Setting Section */}
            <div className="pt-1">
              <button
                onClick={() => setAutoSettingOpen(!autoSettingOpen)}
                className={`sidebar-item w-full justify-between ${isAutoSettingActive ? 'text-[#1A73E8] font-medium' : ''}`}
                data-testid="nav-auto-setting-toggle"
              >
                <span className="flex items-center gap-3">
                  <Sparkles className="w-5 h-5" />
                  Auto Setting
                </span>
                <ChevronRight className={`w-4 h-4 transition-transform ${autoSettingOpen ? 'rotate-90' : ''}`} />
              </button>
              {autoSettingOpen && (
                <div className="ml-4 mt-1 space-y-0.5 border-l-2 border-[#E8F0FE] pl-3">
                  <Link
                    to="/dashboard/auto-post"
                    onClick={() => setSidebarOpen(false)}
                    className={`sidebar-item text-sm ${isActive('/dashboard/auto-post') ? 'active' : ''}`}
                    data-testid="nav-auto-post"
                  >
                    <Zap className="w-4 h-4" />
                    Auto Post
                  </Link>
                  <Link
                    to="/dashboard/auto-reply"
                    onClick={() => setSidebarOpen(false)}
                    className={`sidebar-item text-sm ${isActive('/dashboard/auto-reply') ? 'active' : ''}`}
                    data-testid="nav-auto-reply"
                  >
                    <MessageSquare className="w-4 h-4" />
                    Auto Reply
                  </Link>
                </div>
              )}
            </div>

            {/* Creative Template */}
            <Link
              to="/dashboard/creative-templates"
              onClick={() => setSidebarOpen(false)}
              className={`sidebar-item ${isCreativeActive ? 'active' : ''}`}
              data-testid="nav-creative-templates"
            >
              <Palette className="w-5 h-5" />
              Creative Templates
            </Link>

            {/* Team */}
            <Link
              to="/dashboard/team"
              onClick={() => setSidebarOpen(false)}
              className={`sidebar-item ${isTeamActive ? 'active' : ''}`}
              data-testid="nav-team"
            >
              <Users className="w-5 h-5" />
              Team
            </Link>

            {/* Referrals */}
            <button
              onClick={() => document.getElementById('referral-submenu')?.classList.toggle('hidden')}
              className={`sidebar-item justify-between w-full ${isReferralActive ? 'active' : ''}`}
              data-testid="nav-referrals"
            >
              <span className="flex items-center gap-3">
                <Gift className="w-5 h-5" />
                Referrals
              </span>
              <ChevronDown className="w-4 h-4" />
            </button>
            <div id="referral-submenu" className={`ml-8 space-y-1 ${isReferralActive ? '' : 'hidden'}`}>
              <Link
                to="/dashboard/referrals"
                onClick={() => setSidebarOpen(false)}
                className={`sidebar-item text-sm ${location.pathname === '/dashboard/referrals' ? 'active' : ''}`}
                data-testid="nav-referral-dashboard"
              >
                Dashboard
              </Link>
              <Link
                to="/dashboard/referrals/wallet"
                onClick={() => setSidebarOpen(false)}
                className={`sidebar-item text-sm ${location.pathname === '/dashboard/referrals/wallet' ? 'active' : ''}`}
                data-testid="nav-referral-wallet"
              >
                <Wallet className="w-4 h-4" />
                Wallet
              </Link>
            </div>

            {/* Standalone Items */}
            <div className="pt-2 space-y-1">
              <Link
                to="/dashboard/audit"
                onClick={() => setSidebarOpen(false)}
                className={`sidebar-item ${isActive('/dashboard/audit') ? 'active' : ''}`}
                data-testid="nav-audit"
              >
                <BarChart3 className="w-5 h-5" />
                Profile Audit
              </Link>
              <Link
                to="/dashboard/locations"
                onClick={() => setSidebarOpen(false)}
                className={`sidebar-item ${isActive('/dashboard/locations') ? 'active' : ''}`}
                data-testid="nav-locations"
              >
                <MapPin className="w-5 h-5" />
                Locations
              </Link>
              <Link
                to="/dashboard/analytics"
                onClick={() => setSidebarOpen(false)}
                className={`sidebar-item ${isActive('/dashboard/analytics') ? 'active' : ''}`}
                data-testid="nav-analytics"
              >
                <BarChart3 className="w-5 h-5" />
                Analytics
              </Link>
              <Link
                to="/dashboard/billing"
                onClick={() => setSidebarOpen(false)}
                className={`sidebar-item ${isActive('/dashboard/billing') ? 'active' : ''}`}
                data-testid="nav-billing"
              >
                <CreditCard className="w-5 h-5" />
                Billing
              </Link>
              <Link
                to="/dashboard/settings"
                onClick={() => setSidebarOpen(false)}
                className={`sidebar-item ${isActive('/dashboard/settings') ? 'active' : ''}`}
                data-testid="nav-settings"
              >
                <Settings className="w-5 h-5" />
                Settings
              </Link>
            </div>
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-[#DADCE0]">
            <Link
              to="/pricing"
              className="sidebar-item mb-2"
              data-testid="nav-pricing"
            >
              <Settings className="w-5 h-5" />
              {t('upgradePlan')}
            </Link>
            <button
              onClick={handleLogout}
              className="sidebar-item w-full text-[#D93025] hover:bg-[#FCE8E6] hover:text-[#D93025]"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              {t('signOut')}
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Alert Banner */}
        {alertMessage && (
          <div className="bg-[#FEF7E0] border-b border-[#F9AB00] px-4 py-2 overflow-hidden" data-testid="alert-banner">
            <div className="animate-marquee whitespace-nowrap">
              <span className="text-sm font-medium text-[#202124] inline-block">
                {alertMessage}
              </span>
            </div>
          </div>
        )}
        {/* Top Header */}
        <header className="glass-header sticky top-0 z-30 px-4 lg:px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Mobile Menu + Search */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 text-[#5F6368] hover:text-[#202124] hover:bg-[#F1F3F4] rounded-lg"
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-5 h-5" />
              </button>
              
              <div className="hidden sm:flex items-center gap-2 bg-[#F1F3F4] rounded-lg px-4 py-2">
                <Search className="w-4 h-4 text-[#5F6368]" />
                <input 
                  type="text" 
                  placeholder="Search..."
                  className="bg-transparent outline-none text-sm w-48 lg:w-64"
                />
              </div>
            </div>

            {/* Right Side */}
            <div className="flex items-center gap-4">
              {/* Language Toggle */}
              <button
                onClick={() => setLanguage(language === 'en' ? 'hi' : 'en')}
                className="flex items-center gap-2 px-3 py-2 text-sm text-[#5F6368] hover:bg-[#F1F3F4] rounded-lg transition-colors"
                data-testid="language-toggle"
              >
                <Globe className="w-4 h-4" />
                {language === 'en' ? 'EN' : 'हिं'}
              </button>

              {/* Notifications */}
              <button className="p-2 text-[#5F6368] hover:bg-[#F1F3F4] rounded-lg relative">
                <Bell className="w-5 h-5" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#D93025] rounded-full"></span>
              </button>

              {/* User Profile */}
              <div className="relative">
                <button 
                  onClick={() => setProfileDropdown(!profileDropdown)}
                  className="flex items-center gap-2 p-1 hover:bg-[#F1F3F4] rounded-lg"
                  data-testid="user-menu"
                >
                  <div className="w-8 h-8 bg-[#1A73E8] rounded-full flex items-center justify-center text-white font-medium text-sm">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                  <ChevronDown className="w-4 h-4 text-[#5F6368]" />
                </button>

                {profileDropdown && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-[#DADCE0] py-2">
                    <div className="px-4 py-3 border-b border-[#DADCE0]">
                      <p className="font-medium text-[#202124]">{user?.name}</p>
                      <p className="text-sm text-[#5F6368]">{user?.email}</p>
                      <span className="inline-block mt-2 px-2 py-1 bg-[#E8F0FE] text-[#1A73E8] text-xs font-medium rounded">
                        {user?.membership_plan?.toUpperCase()} PLAN
                      </span>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="w-full px-4 py-2 text-left text-sm text-[#D93025] hover:bg-[#FCE8E6]"
                    >
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
